import React, { Component } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { RadioButton } from 'primereact/radiobutton';
import { ColorPicker } from 'primereact/colorpicker';

import { DROPDOWN_CONFIG } from '../../../shared/Config';

class BarcodeGeneration extends Component {
    state = {
        bulkUpload: false,

        // Single entry form
        rawMaterial: null,
        numberOfPrints: '1',
        rawMaterialBarcode: '',

        // Summary table
        summaryList: [],

        // Dialogs
        showBarcodeConfig: false,
        showPrinterSettings: false,

        // ── Barcode Configuration fields ──
        bgColor: 'FFFFFF',
        textColor: '000000',
        fieldAlignment: 'column',   // 'column' | 'row'
        // config rows table
        configRows: [
            { id: 1, field: 'Raw Material Name', label: 'R', align: 'Center', position: 'Top', fontSize: '5', fontWeight: 'Bold' },
        ],
        // new row form
        newField: 'Custom Label',
        newLabel: '',
        newAlign: 'Center',
        newPosition: 'Top',
        newFontSize: '',
        newFontWeight: 'Normal',

        // ── Printer Settings fields ──
        printerType: null,
        labelConfiguration: null,
        labelHeight: '25',
        labelWidth: '50',
        barcodeHeight: '10',
        barcodeWidth: '48',
        spaceFromTop: '11',
        spaceFromLeft: '5',

        // Bulk upload
        uploadedFileName: '',
    };

    rawMaterialOptions = [
        { label: 'Steel Rod', value: 'steel_rod' },
        { label: 'Copper Wire', value: 'copper_wire' },
        { label: 'Aluminium Sheet', value: 'aluminium_sheet' },
        { label: 'PVC Pipe', value: 'pvc_pipe' },
        { label: 'Rubber Gasket', value: 'rubber_gasket' },
    ];

    printerTypeOptions = [
        { label: 'Label Printer', value: 'label_printer' },
        { label: 'Laser Printer', value: 'laser_printer' },
        { label: 'Thermal Printer', value: 'thermal_printer' },
        { label: 'Dot Matrix', value: 'dot_matrix' },
    ];

    labelConfigOptions = [
        { label: '2 Labels (50 mm × 25 mm)', value: '2_labels_50x25' },
        { label: '1 Label (100 mm × 50 mm)', value: '1_label_100x50' },
        { label: '4 Labels (38 mm × 20 mm)', value: '4_labels_38x20' },
        { label: 'Custom', value: 'custom' },
    ];

    fieldOptions = [
        { label: 'Custom Label', value: 'Custom Label' },
        { label: 'Raw Material Name', value: 'Raw Material Name' },
        { label: 'Barcode', value: 'Barcode' },
        { label: 'Date', value: 'Date' },
        { label: 'Batch No', value: 'Batch No' },
    ];

    alignOptions = [
        { label: 'Left', value: 'Left' },
        { label: 'Center', value: 'Center' },
        { label: 'Right', value: 'Right' },
    ];

    positionOptions = [
        { label: 'Top', value: 'Top' },
        { label: 'Middle', value: 'Middle' },
        { label: 'Bottom', value: 'Bottom' },
    ];

    fontWeightOptions = [
        { label: 'Normal', value: 'Normal' },
        { label: 'Bold', value: 'Bold' },
        { label: 'Light', value: 'Light' },
    ];

    change = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    };

    handleAdd = () => {
        const { rawMaterial, numberOfPrints, rawMaterialBarcode, summaryList } = this.state;
        if (!rawMaterial) { this.toast.show({ severity: 'error', summary: null, detail: 'Select Raw Material before adding.' }); return; }
        if (!rawMaterialBarcode || rawMaterialBarcode.trim() === '') { this.toast.show({ severity: 'error', summary: null, detail: 'Enter Raw Material Barcode before adding.' }); return; }
        if (!numberOfPrints || parseInt(numberOfPrints) < 1) { this.toast.show({ severity: 'error', summary: null, detail: 'Number of prints must be at least 1.' }); return; }
        const label = this.rawMaterialOptions.find(o => o.value === rawMaterial)?.label || rawMaterial;
        this.setState({
            summaryList: [...summaryList, { id: Date.now(), rawMaterial: label, numberOfPrints, rawMaterialBarcode }],
            rawMaterial: null, numberOfPrints: '1', rawMaterialBarcode: '',
        });
        this.toast.show({ severity: 'success', summary: null, detail: 'Raw material added to summary.' });
    };

    handleDelete = (id) => this.setState(prev => ({ summaryList: prev.summaryList.filter(item => item.id !== id) }));
    handleClear = () => this.setState({ summaryList: [] });
    handleDownloadSample = () => this.toast.show({ severity: 'info', summary: null, detail: 'Sample file downloading...' });

    handleBulkFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) { this.setState({ uploadedFileName: file.name }); this.toast.show({ severity: 'success', summary: null, detail: `${file.name} uploaded.` }); }
    };

    addConfigRow = () => {
        const { configRows, newField, newLabel, newAlign, newPosition, newFontSize, newFontWeight } = this.state;
        this.setState({
            configRows: [...configRows, { id: Date.now(), field: newField, label: newLabel, align: newAlign, position: newPosition, fontSize: newFontSize, fontWeight: newFontWeight }],
            newField: 'Custom Label', newLabel: '', newAlign: 'Center', newPosition: 'Top', newFontSize: '', newFontWeight: 'Normal',
        });
    };

    deleteConfigRow = (id) => this.setState(prev => ({ configRows: prev.configRows.filter(r => r.id !== id) }));

    actionBodyTemplate = (rowData) => (
        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-sm p-button-text"
            onClick={() => this.handleDelete(rowData.id)} tooltip="Delete" />
    );

    // ── Realistic CODE128-style barcode SVG ──────────────────────────────────
    renderBarcodeSVG = (svgW = 200, svgH = 60, label = '1000001') => {
        // CODE128 encodes each character as 3 bars + 3 spaces (6 elements, widths 1-4 units)
        // We simulate a realistic pattern: narrow(1) and wide(2-3) bars with matching spaces
        // Pattern: [barWidth, spaceWidth, barWidth, spaceWidth, barWidth, spaceWidth] per char
        const code128Patterns = {
            '0': [2,1,2,2,2,2], '1': [2,2,2,1,2,2], '2': [2,2,2,2,2,1],
            '3': [1,2,1,2,3,2], '4': [1,2,1,3,3,1], '5': [1,3,1,2,3,1],
            '6': [1,2,2,2,1,3], '7': [1,2,2,3,1,2], '8': [1,3,2,2,1,2],
            '9': [2,2,1,2,1,3], 'A': [2,2,1,3,1,2], 'B': [2,3,1,2,1,2],
            '-': [1,1,2,3,2,1], ' ': [1,1,3,2,2,1], '$': [1,1,1,2,3,3],
            'S': [2,1,1,2,1,4], 'E': [2,3,3,1,1,1,2], // start/end guards
        };

        const startGuard = [2,1,1,4];   // narrow-wide-narrow-wide
        const stopGuard  = [2,1,1,2,1,4,2];

        // Build bar pattern from label string
        const buildPattern = (str) => {
            const units = [];
            // quiet zone
            units.push({ type: 'space', w: 3 });
            // start guard
            startGuard.forEach((w, i) => units.push({ type: i % 2 === 0 ? 'bar' : 'space', w }));
            // data
            for (let ch of str) {
                const pat = code128Patterns[ch] || code128Patterns['0'];
                pat.forEach((w, i) => units.push({ type: i % 2 === 0 ? 'bar' : 'space', w }));
            }
            // stop guard
            stopGuard.forEach((w, i) => units.push({ type: i % 2 === 0 ? 'bar' : 'space', w }));
            // quiet zone
            units.push({ type: 'space', w: 3 });
            return units;
        };

        const units = buildPattern(label);
        const totalUnits = units.reduce((s, u) => s + u.w, 0);
        const unitPx = svgW / totalUnits;
        const barH = svgH - 14;

        let x = 0;
        const rects = [];
        units.forEach((u, i) => {
            const w = u.w * unitPx;
            if (u.type === 'bar') {
                rects.push(<rect key={i} x={x} y={0} width={w} height={barH} fill="#111" />);
            }
            x += w;
        });

        return (
            <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
                <rect width={svgW} height={svgH} fill="white" />
                {rects}
                <text x={svgW / 2} y={svgH - 2} textAnchor="middle"
                    fontSize="9" fontFamily="monospace" fill="#333">{label}</text>
            </svg>
        );
    };

    // ── Printer preview with dimension annotations ───────────────────────────
    renderPrinterPreview = () => {
        const { labelHeight, labelWidth, barcodeHeight, barcodeWidth, spaceFromTop, spaceFromLeft } = this.state;

        const lH = Math.max(parseInt(labelHeight) || 25, 10);
        const lW = Math.max(parseInt(labelWidth) || 50, 20);
        const bcH = Math.min(parseInt(barcodeHeight) || 10, lH - 4);
        const bcW = Math.min(parseInt(barcodeWidth) || 48, lW - 4);
        const sTop = parseInt(spaceFromTop) || 11;
        const sLeft = parseInt(spaceFromLeft) || 5;

        // Scale to fit preview container ~280x180px
        const scale = Math.min(260 / lW, 160 / lH);
        const pW = lW * scale;
        const pH = lH * scale;
        const bW = bcW * scale;
        const bH = bcH * scale;
        const bX = sLeft * scale;
        const bY = sTop * scale;

        // annotation pill style
        const pill = (text, x, y, rotate = false) => (
            <g transform={rotate ? `translate(${x},${y}) rotate(-90)` : `translate(${x},${y})`}>
                <rect x={-text.length * 3 - 4} y={-9} width={text.length * 6 + 8} height={16} rx="8" fill="white" stroke="#ccc" strokeWidth="0.8" />
                <text x={0} y={3} textAnchor="middle" fontSize="8" fontFamily="sans-serif" fill="#555">{text}</text>
            </g>
        );

        return (
            <div style={{ position: 'relative', display: 'inline-block' }}>
                <svg width={pW + 80} height={pH + 80} viewBox={`-40 -40 ${pW + 80} ${pH + 80}`}>
                    {/* Label outer box */}
                    <rect x={0} y={0} width={pW} height={pH} fill="white" stroke="#ccc" strokeWidth="1" strokeDasharray="4,3" rx="2" />

                    {/* Barcode inner box */}
                    <rect x={bX} y={bY} width={bW} height={bH} fill="white" stroke="#aaa" strokeWidth="0.8" rx="1" />

                    {/* Barcode bars inside — realistic CODE128 pattern */}
                    {(() => {
                        const pat = [2,1,1,4,2,2,1,2,1,2,3,1,2,1,2,2,1,3,1,2,2,2,2,1,1,2,1,3,2,3,3,1,1,1,2];
                        const total = pat.reduce((s, v) => s + v, 0);
                        const uPx = bW / total;
                        let cx = bX;
                        return pat.map((w, i) => {
                            const rx = cx; cx += w * uPx;
                            return i % 2 === 0
                                ? <rect key={i} x={rx} y={bY} width={Math.max(w * uPx - 0.3, 0.5)} height={bH} fill="#111" />
                                : null;
                        });
                    })()}

                    {/* Dimension annotations */}
                    {/* Top space arrow */}
                    <line x1={bX + bW / 2} y1={0} x2={bX + bW / 2} y2={bY} stroke="#aaa" strokeWidth="0.8" strokeDasharray="2,2" />
                    {pill(`${spaceFromTop || 11}mm`, bX + bW / 2 + 28, bY / 2)}

                    {/* Left space arrow */}
                    <line x1={0} y1={bY + bH / 2} x2={bX} y2={bY + bH / 2} stroke="#aaa" strokeWidth="0.8" strokeDasharray="2,2" />
                    {pill(`${spaceFromLeft || 5}mm`, -20, bY + bH / 2, true)}

                    {/* Label width */}
                    <line x1={0} y1={-20} x2={pW} y2={-20} stroke="#aaa" strokeWidth="0.8" />
                    <line x1={0} y1={-24} x2={0} y2={-16} stroke="#aaa" strokeWidth="0.8" />
                    <line x1={pW} y1={-24} x2={pW} y2={-16} stroke="#aaa" strokeWidth="0.8" />
                    {pill(`${lW}mm`, pW / 2, -20)}

                    {/* Label height */}
                    <line x1={pW + 20} y1={0} x2={pW + 20} y2={pH} stroke="#aaa" strokeWidth="0.8" />
                    <line x1={pW + 16} y1={0} x2={pW + 24} y2={0} stroke="#aaa" strokeWidth="0.8" />
                    <line x1={pW + 16} y1={pH} x2={pW + 24} y2={pH} stroke="#aaa" strokeWidth="0.8" />
                    {pill(`${lH}mm`, pW + 20, pH / 2, true)}

                    {/* Barcode width */}
                    <line x1={bX} y1={bY - 12} x2={bX + bW} y2={bY - 12} stroke="#aaa" strokeWidth="0.8" />
                    <line x1={bX} y1={bY - 16} x2={bX} y2={bY - 8} stroke="#aaa" strokeWidth="0.8" />
                    <line x1={bX + bW} y1={bY - 16} x2={bX + bW} y2={bY - 8} stroke="#aaa" strokeWidth="0.8" />
                    {pill(`${bcW}mm`, bX + bW / 2, bY - 12)}

                    {/* Barcode height */}
                    <line x1={bX - 12} y1={bY} x2={bX - 12} y2={bY + bH} stroke="#aaa" strokeWidth="0.8" />
                    <line x1={bX - 16} y1={bY} x2={bX - 8} y2={bY} stroke="#aaa" strokeWidth="0.8" />
                    <line x1={bX - 16} y1={bY + bH} x2={bX - 8} y2={bY + bH} stroke="#aaa" strokeWidth="0.8" />
                    {pill(`${bcH}mm`, bX - 12, bY + bH / 2, true)}
                </svg>
            </div>
        );
    };

    render() {
        const {
            bulkUpload, rawMaterial, numberOfPrints, rawMaterialBarcode,
            summaryList, showBarcodeConfig, showPrinterSettings,
            bgColor, textColor, fieldAlignment,
            configRows, newField, newLabel, newAlign, newPosition, newFontSize, newFontWeight,
            printerType, labelConfiguration, labelHeight, labelWidth,
            barcodeHeight, barcodeWidth, spaceFromTop, spaceFromLeft,
            uploadedFileName,
        } = this.state;

        const smallDD = (name, value, options) => (
            <Dropdown style={{ width: '100%', minWidth: '90px' }}
                options={options} value={value} name={name} onChange={this.change}
                placeholder="-" />
        );

        // ── Dialog footers ────────────────────────────────────────────────────
        const barcodeConfigFooter = (
            <div className="p-d-flex p-jc-end" style={{ gap: '10px' }}>
                <Button style={{float:'right'}} label="Cancel" icon="pi pi-times" className="ml1 p-button-secondary" 
                    onClick={() => this.setState({ showBarcodeConfig: false })} />

                <Button label="Save Changes" className="p-button-success"
                    onClick={() => { this.toast.show({ severity: 'success', summary: null, detail: 'Barcode configuration saved.' }); this.setState({ showBarcodeConfig: false }); }} />
            </div>
        );

        const printerSettingsFooter = (
            <div className="p-d-flex p-jc-end" style={{ gap: '10px' }}>
                <Button label="Cancel" className="p-button p-button-secondary"icon="pi pi-times"
                    onClick={() => this.setState({ showPrinterSettings: false })} />
                <Button label="Save" className="p-button p-button-success"
                    onClick={() => { this.toast.show({ severity: 'success', summary: null, detail: 'Printer settings saved.' }); this.setState({ showPrinterSettings: false }); }} />
            </div>
        );

        const thStyle = { background: '#f0f4ff', fontWeight: 700, fontSize: '0.8rem', color: '#333', padding: '12px 10px', borderBottom: '1px solid #dde3f0', textAlign: 'left' };
        const tdStyle = { padding: '10px 8px', fontSize: '0.82rem', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle', color: '#333' };

        return (
            <div className="p-grid">
                <Toast ref={(el) => this.toast = el} />

                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>

                        {/* ── Page Header ── */}
                        <div className="p-d-flex p-ai-center p-jc-between" style={{ marginBottom: '20px' }}>
                            <h1 style={{ margin: 0 }}>Barcode Generation</h1>
                            <div className="p-d-flex p-ai-center" style={{ gap: '10px' }}>
                                <Button label="Barcode Configuration" className="p-button-outlined p-button-danger"
                                    onClick={() => this.setState({ showBarcodeConfig: true })} />
                                <Button label="Printer Settings" className="p-button-outlined p-button-danger"
                                    onClick={() => this.setState({ showPrinterSettings: true })} />
                            </div>
                        </div>

                        {/* ── Select Raw Materials ── */}
                        <div className="card" style={{ marginBottom: '20px', border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                            <div className="p-d-flex p-ai-center p-jc-between" style={{ marginBottom: '16px' }}>
                                <h3 style={{ margin: 0, fontWeight: 600, fontSize: '1rem' }}>Select Raw Materials</h3>
                                <div className="p-d-flex p-ai-center" style={{ gap: '10px' }}>
                                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#555' }}>Bulk Upload</span>
                                    <InputSwitch checked={bulkUpload}
                                        onChange={(e) => this.setState({ bulkUpload: e.value, uploadedFileName: '' })} />
                                </div>
                            </div>

                            {!bulkUpload && (
                                <div className="p-grid" style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: '8px', margin: 0, padding: '16px' }}>
                                    <div className="p-col-12 p-md-4">
                                        <label>Raw Material <span className="requiredColor">*</span>
                                            <i className="pi pi-info-circle" style={{ fontSize: '0.75rem', color: '#9e9e9e', marginLeft: '5px' }} />
                                        </label>
                                        <div style={{ paddingTop: '6px' }}>
                                            <Dropdown className="width100" placeholder="Select Raw Material"
                                                options={this.rawMaterialOptions} value={rawMaterial}
                                                name="rawMaterial" onChange={this.change} {...DROPDOWN_CONFIG} />
                                        </div>
                                    </div>
                                    <div className="p-col-12 p-md-3">
                                        <label>Number of prints <span className="requiredColor">*</span>
                                            <i className="pi pi-info-circle" style={{ fontSize: '0.75rem', color: '#9e9e9e', marginLeft: '5px' }} />
                                        </label>
                                        <div style={{ paddingTop: '6px' }}>
                                            <InputText className="width100" name="numberOfPrints" placeholder="1"
                                                value={numberOfPrints} onChange={this.change} keyfilter="pint" />
                                        </div>
                                    </div>
                                    <div className="p-col-12 p-md-4">
                                        <label>Raw Material Barcode <span className="requiredColor">*</span>
                                            <i className="pi pi-info-circle" style={{ fontSize: '0.75rem', color: '#9e9e9e', marginLeft: '5px' }} />
                                        </label>
                                        <div style={{ paddingTop: '6px' }}>
                                            <InputText className="width100" name="rawMaterialBarcode"
                                                placeholder="Enter barcode" value={rawMaterialBarcode} onChange={this.change} />
                                        </div>
                                    </div>
                                    <div className="p-col-12 p-md-1 p-d-flex p-ai-end p-jc-end">
                                        <Button label="Add" className="p-button-secondary" onClick={this.handleAdd} />
                                    </div>
                                </div>
                            )}

                            {bulkUpload && (
                                <div className="p-grid mt1">
                                    <div className="p-col-12 p-md-3">
                                        <div onClick={this.handleDownloadSample} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', border: '1.5px dashed #e0e0e0', borderRadius: '8px', padding: '24px 16px', background: '#fafafa', cursor: 'pointer', minHeight: '100px' }}>
                                            <i className="pi pi-download" style={{ fontSize: '1.5rem', color: '#9e9e9e' }} />
                                            <span style={{ fontSize: '0.875rem', color: '#555', fontWeight: 500 }}>Download sample file</span>
                                        </div>
                                    </div>
                                    <div className="p-col-12 p-md-3">
                                        <label htmlFor="bulk-file-input" style={{ cursor: 'pointer', display: 'block' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', border: '1.5px dashed #e0e0e0', borderRadius: '8px', padding: '24px 16px', background: '#fafafa', cursor: 'pointer', minHeight: '100px' }}>
                                                <i className="pi pi-upload" style={{ fontSize: '1.5rem', color: '#9e9e9e' }} />
                                                <span style={{ fontSize: '0.875rem', color: '#555', fontWeight: 500 }}>{uploadedFileName || 'Upload excel file'}</span>
                                            </div>
                                        </label>
                                        <input id="bulk-file-input" type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={this.handleBulkFileUpload} />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── Summary DataTable ── */}
                        <div className="card" style={{ border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                            <div className="p-d-flex p-ai-center p-jc-between" style={{ marginBottom: '12px' }}>
                                <h3 style={{ margin: 0, fontWeight: 600, fontSize: '1rem' }}>Selected Raw Material Summary</h3>
                                {summaryList.length > 0 && (
                                    <Button label="Clear" className="p-button-text p-button-danger"
                                        style={{ fontSize: '0.813rem', fontWeight: 600, padding: '4px 8px' }}
                                        onClick={this.handleClear} />
                                )}
                            </div>
                            <DataTable value={summaryList} emptyMessage="No raw materials added yet." rowHover>
                                <Column field="rawMaterial" header="Raw Material" />
                                <Column field="numberOfPrints" header="Number Of Prints" />
                                <Column field="rawMaterialBarcode" header="Raw Material Barcode" />
                                <Column header="Action" body={this.actionBodyTemplate} style={{ width: '80px', textAlign: 'center' }} />
                            </DataTable>
                            {summaryList.length > 0 && (
                                <div className="p-d-flex p-jc-end mt1">
                                    <Button label="Generate Barcodes" icon="pi pi-barcode" className="p-button-danger"
                                        onClick={() => this.toast.show({ severity: 'success', summary: null, detail: `${summaryList.length} barcode(s) generated.` })} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ══════════════════════════════════════════════════════════
                    BARCODE CONFIGURATION MODAL
                ══════════════════════════════════════════════════════════ */}
                <Dialog
                    header="Barcode Configuration"
                    visible={showBarcodeConfig}
                    style={{ width: '85vw', maxWidth: '1100px' }}
                    maximizable modal
                    footer={barcodeConfigFooter}
                    onHide={() => this.setState({ showBarcodeConfig: false })}
                >
                    <div className="p-grid" style={{ minHeight: '460px' }}>

                        {/* Left — Form */}
                        <div className="p-col-12 p-md-8" style={{ borderRight: '1px solid #e0e0e0', paddingRight: '24px' }}>

                            {/* Colors + Alignment — single flex row matching screenshot */}
                            <div className="p-d-flex p-ai-end" style={{ gap: '32px', marginBottom: '20px', paddingBottom: '18px', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap' }}>

                                {/* Background Color */}
                                <div>
                                    <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#333', display: 'block', marginBottom: '8px' }}>Background Color</label>
                                    <div className="p-d-flex p-ai-center" style={{ gap: '8px', border: '1px solid #d0d0d0', borderRadius: '6px', padding: '7px 12px', background: '#fff', width: '160px' }}>
                                        <ColorPicker value={bgColor} onChange={(e) => this.setState({ bgColor: e.value })} />
                                        <InputText
                                            value={`#${(bgColor || 'FFFFFF').toUpperCase()}`}
                                            readOnly
                                            style={{ border: 'none', padding: 0, fontSize: '0.85rem', width: '80px', fontFamily: 'monospace', background: 'transparent', boxShadow: 'none' }}
                                        />
                                    </div>
                                </div>

                                {/* Text Color */}
                                <div>
                                    <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#333', display: 'block', marginBottom: '8px' }}>Text Color</label>
                                    <div className="p-d-flex p-ai-center" style={{ gap: '8px', border: '1px solid #d0d0d0', borderRadius: '6px', padding: '7px 12px', background: '#fff', width: '160px' }}>
                                        <ColorPicker value={textColor} onChange={(e) => this.setState({ textColor: e.value })} />
                                        <InputText
                                            value={`#${(textColor || '000000').toUpperCase()}`}
                                            readOnly
                                            style={{ border: 'none', padding: 0, fontSize: '0.85rem', width: '80px', fontFamily: 'monospace', background: 'transparent', boxShadow: 'none' }}
                                        />
                                    </div>
                                </div>

                                {/* Alignment of fields */}
                                <div>
                                    <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#333', display: 'block', marginBottom: '8px' }}>Alignment of fields</label>
                                    <div className="p-d-flex p-ai-center" style={{ gap: '20px', height: '38px' }}>
                                        <div className="p-d-flex p-ai-center" style={{ gap: '8px' }}>
                                            <RadioButton inputId="alignCol" name="fieldAlignment" value="column" onChange={this.change} checked={fieldAlignment === 'column'} />
                                            <label htmlFor="alignCol" style={{ cursor: 'pointer', fontSize: '0.875rem', margin: 0 }}>Column</label>
                                        </div>
                                        <div className="p-d-flex p-ai-center" style={{ gap: '8px' }}>
                                            <RadioButton inputId="alignRow" name="fieldAlignment" value="row" onChange={this.change} checked={fieldAlignment === 'row'} />
                                            <label htmlFor="alignRow" style={{ cursor: 'pointer', fontSize: '0.875rem', margin: 0 }}>Row</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Fields config table */}
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                    <colgroup>
                                        <col style={{ width: '22%' }} />
                                        <col style={{ width: '10%' }} />
                                        <col style={{ width: '14%' }} />
                                        <col style={{ width: '13%' }} />
                                        <col style={{ width: '9%' }} />
                                        <col style={{ width: '16%' }} />
                                        <col style={{ width: '10%' }} />
                                    </colgroup>
                                    <thead>
                                        <tr style={{ background: '#f5f6fa' }}>
                                            <th style={thStyle}>Field</th>
                                            <th style={thStyle}>Label</th>
                                            <th style={thStyle}>Align</th>
                                            <th style={thStyle}>Position</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Font{'\n'}Size</th>
                                            <th style={thStyle}>Font Weight</th>
                                            <th style={thStyle}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* New row entry */}
                                        <tr style={{ background: '#fff', borderBottom: '1px solid #e8e8e8' }}>
                                            <td style={tdStyle}>{smallDD('newField', newField, this.fieldOptions)}</td>
                                            <td style={tdStyle}>
                                                <InputText value={newLabel} name="newLabel" onChange={this.change}
                                                    style={{ width: '100%' }} />
                                            </td>
                                            <td style={tdStyle}>{smallDD('newAlign', newAlign, this.alignOptions)}</td>
                                            <td style={tdStyle}>{smallDD('newPosition', newPosition, this.positionOptions)}</td>
                                            <td style={tdStyle}>
                                                <InputText value={newFontSize} name="newFontSize" onChange={this.change}
                                                    keyfilter="pint" style={{ width: '100%' }} placeholder="px" />
                                            </td>
                                            <td style={tdStyle}>{smallDD('newFontWeight', newFontWeight, this.fontWeightOptions)}</td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <Button label="Add" className="p-button-outlined p-button-danger p-button-sm"
                                                    onClick={this.addConfigRow} />
                                            </td>
                                        </tr>

                                        {/* Existing saved rows */}
                                        {configRows.map((row) => (
                                            <tr key={row.id} style={{ background: '#fff' }}>
                                                <td style={tdStyle}>
                                                    <div className="p-d-flex p-ai-center" style={{ gap: '8px' }}>
                                                        <i className="pi pi-bars" style={{ color: '#bbb', cursor: 'grab', fontSize: '0.8rem', flexShrink: 0 }} />
                                                        <span style={{ fontSize: '0.82rem', color: '#333' }}>{row.field}</span>
                                                    </div>
                                                </td>
                                                <td style={{ ...tdStyle, textAlign: 'center', color: '#333', fontSize: '0.82rem' }}>{row.label}</td>
                                                <td style={{ ...tdStyle, color: '#333', fontSize: '0.82rem' }}>{row.align}</td>
                                                <td style={{ ...tdStyle, color: '#333', fontSize: '0.82rem' }}>{row.position}</td>
                                                <td style={{ ...tdStyle, textAlign: 'center', color: '#333', fontSize: '0.82rem' }}>{row.fontSize}</td>
                                                <td style={{ ...tdStyle, color: '#333', fontSize: '0.82rem' }}>{row.fontWeight}</td>
                                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                    <Button icon="pi pi-trash"
                                                        className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                                        onClick={() => this.deleteConfigRow(row.id)} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Right — Barcode Preview */}
                        <div className="p-col-12 p-md-4 p-d-flex" style={{ flexDirection: 'column', alignItems: 'center', paddingLeft: '20px' }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#555', marginBottom: '16px', textAlign: 'center' }}>Barcode Preview</div>
                            <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px 16px', background: `#${bgColor}`, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '100%' }}>
                                {/* Fields above barcode */}
                                {configRows.filter(r => r.position === 'Top').map((row, i) => (
                                    <div key={i} style={{
                                        fontSize: row.fontSize ? `${row.fontSize}px` : '10px',
                                        fontWeight: row.fontWeight === 'Bold' ? 700 : row.fontWeight === 'Light' ? 300 : 400,
                                        color: `#${textColor}`,
                                        textAlign: row.align === 'Center' ? 'center' : row.align === 'Right' ? 'right' : 'left',
                                        width: '100%',
                                    }}>
                                        {row.label || row.field}
                                    </div>
                                ))}
                                {/* Barcode */}
                                {this.renderBarcodeSVG(180, 55, '1000001')}
                                {/* Fields below barcode */}
                                {configRows.filter(r => r.position !== 'Top').map((row, i) => (
                                    <div key={i} style={{
                                        fontSize: row.fontSize ? `${row.fontSize}px` : '10px',
                                        fontWeight: row.fontWeight === 'Bold' ? 700 : 400,
                                        color: `#${textColor}`,
                                        textAlign: row.align === 'Center' ? 'center' : 'left',
                                        width: '100%',
                                    }}>
                                        {row.label || row.field}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Dialog>

                {/* ══════════════════════════════════════════════════════════
                    PRINTER SETTINGS MODAL
                ══════════════════════════════════════════════════════════ */}
                <Dialog
                    header="Printer Settings"
                    visible={showPrinterSettings}
                    style={{ width: '75vw', maxWidth: '900px' }}
                    maximizable modal
                    footer={printerSettingsFooter}
                    onHide={() => this.setState({ showPrinterSettings: false })}
                >
                    <div className="p-grid">
                        {/* Fields */}
                        <div className="p-col-12">
                            <div className="p-grid">
                                <div className="p-col-12 p-md-6">
                                    <label>Select Printer Type <span className="requiredColor">*</span></label>
                                    <div style={{ paddingTop: '6px' }}>
                                        <Dropdown className="width100" placeholder="Select Printer Type"
                                            options={this.printerTypeOptions} value={printerType}
                                            name="printerType" onChange={this.change} {...DROPDOWN_CONFIG} />
                                    </div>
                                </div>
                                <div className="p-col-12 p-md-6">
                                    <label>Label Configuration <span className="requiredColor">*</span></label>
                                    <div style={{ paddingTop: '6px' }}>
                                        <Dropdown className="width100" placeholder="Select Label Configuration"
                                            options={this.labelConfigOptions} value={labelConfiguration}
                                            name="labelConfiguration" onChange={this.change} {...DROPDOWN_CONFIG} />
                                    </div>
                                </div>
                                <div className="p-col-12 p-md-6">
                                    <label>Label Height <span className="requiredColor">*</span></label>
                                    <div style={{ paddingTop: '6px' }}>
                                        <InputText className="width100" name="labelHeight" value={labelHeight} onChange={this.change} keyfilter="pnum" />
                                    </div>
                                </div>
                                <div className="p-col-12 p-md-6">
                                    <label>Label Width <span className="requiredColor">*</span></label>
                                    <div style={{ paddingTop: '6px' }}>
                                        <InputText className="width100" name="labelWidth" value={labelWidth} onChange={this.change} keyfilter="pnum" />
                                    </div>
                                </div>
                                <div className="p-col-12 p-md-6">
                                    <label>Barcode Height <span className="requiredColor">*</span></label>
                                    <div style={{ paddingTop: '6px' }}>
                                        <InputText className="width100" name="barcodeHeight" value={barcodeHeight} onChange={this.change} keyfilter="pnum" />
                                    </div>
                                </div>
                                <div className="p-col-12 p-md-6">
                                    <label>Barcode Width <span className="requiredColor">*</span></label>
                                    <div style={{ paddingTop: '6px' }}>
                                        <InputText className="width100" name="barcodeWidth" value={barcodeWidth} onChange={this.change} keyfilter="pnum" />
                                    </div>
                                </div>
                                <div className="p-col-12 p-md-6">
                                    <label>Space From Top <span className="requiredColor">*</span></label>
                                    <div style={{ paddingTop: '6px' }}>
                                        <InputText className="width100" name="spaceFromTop" value={spaceFromTop} onChange={this.change} keyfilter="pnum" />
                                    </div>
                                </div>
                                <div className="p-col-12 p-md-6">
                                    <label>Space From Left <span className="requiredColor">*</span></label>
                                    <div style={{ paddingTop: '6px' }}>
                                        <InputText className="width100" name="spaceFromLeft" value={spaceFromLeft} onChange={this.change} keyfilter="pnum" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Barcode Preview with dimension annotations */}
                        <div className="p-col-12" style={{ paddingTop: '4px' }}>
                            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: '16px' }}>Barcode Preview</label>
                            <div style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px', display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
                                {this.renderPrinterPreview()}
                            </div>
                        </div>
                    </div>
                </Dialog>

            </div>
        );
    }
}

export default BarcodeGeneration;